import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, WOOD, cloth, emis, metal, std, stone } from '../materials.ts';
import { G, M, V, glowSprite, limb } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import { boxClad, pitchedRoof, seams, type Era2Material } from './era2.ts';
import { ASHLAR, IRON_BAND, IRON_PLATE, IRON_RIVET, IRON_STONE, crack, plate, rubbleAround, snapTop, splinters, stain, type Wear } from './structures.ts';
import { LIGHT_K, begin, done, pointLight, type BaseOpts, type Model } from './types.ts';

/*
 * Buildings of the terraced land (docs/CONSTRUCCION.md): the gathering post where golems unload what they
 * bring in, and the golem works that makes them. Each takes three cells, a triangle, so both stand on a
 * rounded triangular yard: the long side at the back carries the building, the point toward the field the
 * work in the open. Like the era II buildings they draw their three materials themselves (1 timber and
 * fieldstone, 2 dressed stone, 3 stone bound in iron), their wear (1 worn, 2 crumbling) and their ruin.
 */

export interface LandOpts extends BaseOpts {
  material?: Era2Material;
  wear?: Wear;
}

export interface GolemWorksOpts extends LandOpts {
  /** Each level adds a module: 2 a second cradle with its golem, 3 the tall rune chimney. */
  level?: 1 | 2 | 3;
}

const TAU = Math.PI * 2;
export const PLINTH = stone('#5f5b55');
export const EARTH = std('#4a4034', { roughness: 1, flatShading: true });
export const PLANK = std('#4a2f22', { roughness: 0.95, flatShading: true });
export const PLANK_DK = std('#3a2419', { roughness: 1, flatShading: true });
export const IRON_ROOF = metal('#3d434b', { roughness: 0.42, metalness: 0.6, flatShading: true });
export const GAP = std('#120e0b', { roughness: 1 });
export const LOG_END = std('#a07850', { roughness: 0.9 });
export const BLOCK = stone('#aaa49a');
export const SACK = cloth('#6a5f50');
export const FORGE = emis('#ffb070', '#ff5a10', 1.4);
export const EMBER = emis('#ff9a50', '#ff6a1a', 0.5);
/** Crystal as the golems carry it from the veins. */
export const CRYSTAL = emis('#bfe8ff', '#3aa6ff', 0.9, { flatShading: true });
/** The golems' own stone and rune light (heroes.golem). */
export const GOLEM = stone('#7a7266');
export const RUNE = emis('#8fe9ff', '#2fbfe0', 0.9);
export const RUNE_STONE = stone('#9a9488');

export const box = (parent: THREE.Object3D, mat: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
  const m = M(new THREE.BoxGeometry(w, h, d), mat, parent);
  m.position.set(x, y, z);
  m.rotation.y = ry;
  return m;
};

export const wallOf = (mat: Era2Material): THREE.MeshStandardMaterial => (mat === 1 ? stone('#77736c') : stone(mat === 3 ? IRON_STONE : '#8b857a'));
export const quoinOf = (mat: Era2Material): THREE.MeshStandardMaterial => stone(mat === 3 ? '#868a90' : '#a39d92');
export const rubbleColors = (mat: Era2Material): string[] => (mat === 1 ? ['#77736c', '#6b675f', '#837e75'] : mat === 3 ? ['#72767c', '#5d6167', '#868a90'] : ASHLAR);

/** Height of the yard's top. */
export const Y0 = 0.07;
/** Corners of the triangular yard: two at the back, one toward the field. */
const YARD_R = 0.48;
const YARD_ROUND = 0.36;

/**
 * The yard: a rounded triangle (the hull of three discs) with its long side at the back. Packed earth for a
 * timber building, a flagstone footing for stone and iron.
 */
export const yard = (g: THREE.Object3D, mat: Era2Material): void => {
  const s = new THREE.Shape();
  // Shape space (x, y) lies flat as world (x, −z): the corner at −y points to the field, the two at +y are the back.
  for (const phi of [-Math.PI / 2, Math.PI / 6, (5 * Math.PI) / 6]) {
    s.absarc(Math.cos(phi) * YARD_R, Math.sin(phi) * YARD_R, YARD_ROUND, phi - Math.PI / 3, phi + Math.PI / 3, false);
  }
  s.closePath();
  const geo = new THREE.ExtrudeGeometry(s, { depth: Y0, bevelEnabled: false, curveSegments: 5 });
  const m = M(geo, mat === 1 ? EARTH : PLINTH, g);
  m.rotation.x = -Math.PI / 2;
  if (mat === 3) {
    // An iron kerb along the back edge.
    box(g, IRON_BAND, YARD_R * 1.75, 0.03, 0.02, 0, Y0 - 0.012, -YARD_R / 2 - YARD_ROUND - 0.006);
  }
};

/** A right-triangle infill (the side of a lean-to): `d` deep, `h` high at the back, `thick` thick, at x. */
export const wedge = (parent: THREE.Object3D, mat: THREE.Material, d: number, h: number, thick: number, x: number, y0: number, zBack: number): void => {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.lineTo(d, 0);
  s.lineTo(0, h);
  s.closePath();
  const m = M(new THREE.ExtrudeGeometry(s, { depth: thick, bevelEnabled: false }), mat, parent);
  m.rotation.y = -Math.PI / 2;
  m.position.set(x + thick / 2, y0, zBack);
};

/** Quoins up the four corners of a box body. */
export const quoins = (parent: THREE.Object3D, mat: THREE.Material, cx: number, cz: number, w: number, d: number, y0: number, y1: number): void => {
  const n = Math.floor((y1 - y0) / 0.09);
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    for (let i = 0; i < n; i++) {
      const long = (i + (sx * sz > 0 ? 0 : 1)) % 2 === 0;
      box(parent, mat, long ? 0.13 : 0.07, 0.075, long ? 0.07 : 0.13, cx + sx * (w / 2 - (long ? 0.055 : 0.025) + 0.012), y0 + 0.045 + i * 0.09, cz + sz * (d / 2 - (long ? 0.025 : 0.055)));
    }
  }
};

/** Proud fieldstones on a face (front: ry 0) of a timber-and-fieldstone wall. */
export const fieldstones = (parent: THREE.Object3D, n: number, x0: number, x1: number, y0: number, y1: number, z: number, rng: () => number): void => {
  const fs = [PLINTH, stone('#837e75')];
  for (let i = 0; i < n; i++) {
    const m = M(new THREE.DodecahedronGeometry(0.03 + rng() * 0.03, 0), fs[i % 2] as THREE.Material, parent);
    m.scale.z = 0.45;
    m.position.set(x0 + rng() * (x1 - x0), y0 + rng() * (y1 - y0), z);
  }
};

/** A wooden crate with dark edges. */
export const crate = (parent: THREE.Object3D, s: number, x: number, y: number, z: number, ry: number): THREE.Group => {
  const cg = G(parent, x, y, z);
  cg.rotation.y = ry;
  box(cg, WOOD, s, s * 0.8, s, 0, s * 0.4, 0);
  for (const k of [-1, 1]) box(cg, DWOOD, s + 0.01, 0.014, 0.014, 0, s * 0.4 + k * s * 0.3, s / 2 + 0.002);
  box(cg, DWOOD, 0.014, s * 0.82, 0.014, 0, s * 0.4, s / 2 + 0.004).rotation.z = 0.7;
  return cg;
};

/* ---------- the gathering post ---------- */

/**
 * The gathering post: a long lean-to across the back of the yard, open toward the field, its plank roof
 * sloping forward over sacks and crates; a log stack in front of the left wall, cut blocks on a pallet under
 * a timber hoist on the right, a crate of pale crystal shards and a hand cart at the point of the yard.
 * Plank walls on fieldstone in wood, dressed stone walls in stone, banded and plated with an iron roof in
 * iron. Worn (1): stains and cracks, a few boards gone from the roof. Crumbling (2): a front post knocked
 * out and the roof sagging, holes in it, the log stack spilled, the hoist's jib snapped, a wheel off the cart.
 */
export const depot = (o?: LandOpts): Model => {
  const mat = o?.material ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.85, 131);
  const { g } = c;
  const wr = mulberry32(1310 + mat * 3 + wear);
  const iron = mat === 3;
  yard(g, mat);

  // The lean-to.
  const SW = 1.04, SD = 0.4, SZ = -0.37;
  const back = SZ - SD / 2, front = SZ + SD / 2;
  const backH = 0.62, frontH = 0.42;
  const wallM = mat === 1 ? WOOD : wallOf(mat);
  const postM = iron ? IRON_BAND : DWOOD;
  box(g, wallM, SW, backH, 0.06, 0, Y0 + backH / 2, back + 0.03);
  for (const sx of [-1, 1]) {
    box(g, wallM, 0.06, frontH, SD, sx * (SW / 2 - 0.03), Y0 + frontH / 2, SZ);
    wedge(g, wallM, SD, backH - frontH, 0.06, sx * (SW / 2 - 0.03), Y0 + frontH, back);
  }
  if (mat === 1) {
    // Plank walls: battens down the back and the sides, a fieldstone sill.
    for (let x = -SW / 2 + 0.1; x < SW / 2 - 0.05; x += 0.13) box(g, DWOOD, 0.018, backH, 0.012, x, Y0 + backH / 2, back - 0.004);
    for (const sx of [-1, 1]) for (const z of [-0.1, 0.06]) box(g, DWOOD, 0.012, frontH, 0.018, sx * (SW / 2 + 0.004), Y0 + frontH / 2, SZ + z);
  } else {
    quoins(g, quoinOf(mat), 0, SZ, SW, SD, Y0, Y0 + frontH);
  }
  // Front posts and the beam under the eave.
  const posts = [-SW / 2 + 0.03, -0.17, 0.17, SW / 2 - 0.03];
  const sagging = wear === 2;
  posts.forEach((x, i) => {
    if (sagging && i === 2) {
      // Knocked out: the post lies in the yard.
      const p = box(g, postM, 0.05, 0.05, frontH, x + 0.08, Y0 + 0.03, front + 0.25, 0.5);
      p.rotation.x = 0.04;
      return;
    }
    box(g, postM, 0.05, frontH, 0.05, x, Y0 + frontH / 2, front);
  });
  box(g, postM, SW + 0.04, 0.05, 0.05, 0, Y0 + frontH, front);
  if (iron) posts.forEach((x, i) => { if (!(sagging && i === 2)) for (const y of [0.12, 0.32]) box(g, IRON_RIVET, 0.06, 0.012, 0.06, x, Y0 + y, front); });

  // The roof: one slope of planks (iron sheet in iron) from the back wall down past the posts.
  const rise = backH - frontH;
  const over = 0.1;
  const run = SD + over;
  const angle = Math.atan2(rise, SD);
  const L = Math.hypot(run, rise + over * Math.tan(angle));
  const roof = G(g, 0, Y0 + backH - (rise + over * Math.tan(angle)) / 2 + 0.02, back + run / 2 - 0.02);
  roof.rotation.x = angle;
  if (sagging) {
    // The right half droops where its post went.
    roof.rotation.z = -0.07;
    roof.position.y -= 0.03;
  }
  const roofM = iron ? IRON_ROOF : PLANK;
  box(roof, roofM, SW + 0.12, 0.035, L, 0, 0, 0);
  if (iron) {
    for (let x = -SW / 2; x <= SW / 2 + 0.01; x += 0.13) box(roof, IRON_BAND, 0.016, 0.022, L, x, 0.026, 0);
  } else {
    for (let x = -SW / 2 + 0.04; x < SW / 2; x += 0.11) box(roof, PLANK_DK, 0.012, 0.012, L, x + (wr() - 0.5) * 0.02, 0.022, 0);
    box(roof, DWOOD, SW + 0.14, 0.03, 0.035, 0, 0.01, L / 2 - 0.01);
  }
  for (let i = 0; i < wear * 2; i++) {
    box(roof, GAP, 0.07 + wr() * 0.05, 0.01, 0.12 + wr() * 0.1, (wr() - 0.5) * 0.8, 0.02, (wr() - 0.5) * L * 0.6);
  }
  if (wear === 2) {
    box(roof, GAP, 0.22, 0.012, 0.2, 0.26, 0.024, 0.02);
    for (const x of [0.2, 0.32]) box(roof, DWOOD, 0.03, 0.03, 0.24, x, 0.03, 0.02);
  }

  // Under the roof: sacks, crates and a cask.
  for (const [x, z, s] of [[-0.36, -0.42, 0.075], [-0.25, -0.44, 0.07], [-0.31, -0.33, 0.065]] as const) {
    const sk = M(new THREE.DodecahedronGeometry(s, 0), SACK, g);
    sk.position.set(x, Y0 + s * 0.7, z);
    sk.scale.set(1, 0.8, 0.85);
  }
  crate(g, 0.15, 0.3, Y0, -0.42, 0.1);
  crate(g, 0.12, 0.31, Y0 + 0.12, -0.43, -0.2);
  crate(g, 0.13, 0.1, Y0, -0.45, 0.4);
  const cask = M(new THREE.CylinderGeometry(0.055, 0.055, 0.14, 9), WOOD, g);
  cask.position.set(-0.08, Y0 + 0.07, -0.42);
  for (const y of [0.035, 0.105]) {
    const r = M(new THREE.TorusGeometry(0.057, 0.006, 3, 12), DSTEEL, g);
    r.rotation.x = Math.PI / 2;
    r.position.set(-0.08, Y0 + y, -0.42);
  }
  // A lantern hung from the eave beam.
  const lan = G(g, -0.17, Y0 + frontH - 0.1, front + 0.03);
  limb(lan, V(0, 0.07, 0), V(0, 0.03, 0), 0.004, 0.004, DSTEEL, 3);
  M(new THREE.CylinderGeometry(0.025, 0.03, 0.05, 6, 1, true), DSTEEL, lan);
  box(lan, EMBER, 0.022, 0.03, 0.022, 0, 0, 0);
  M(new THREE.ConeGeometry(0.035, 0.03, 6), DSTEEL, lan).position.y = 0.04;
  glowSprite(lan, '#ff8a3a', 0.22, V(0, 0, 0.01), [0.7, 0.55, 0.3][wear] as number);

  // The log stack, left: round ends toward the field.
  const lx = -0.44, lz = 0.03;
  const logR = 0.045, logLen = 0.36;
  const logAt = (x: number, y: number, z: number, ry: number, rx = 0): void => {
    const lg = G(g, x, y, z);
    lg.rotation.set(rx, ry, 0);
    const cy = M(new THREE.CylinderGeometry(logR, logR * 1.05, logLen, 7), WOOD, lg);
    cy.rotation.x = Math.PI / 2;
    const end = M(new THREE.CircleGeometry(logR * 0.92, 7), LOG_END, lg);
    end.position.z = logLen / 2 + 0.002;
  };
  const rows = [4, 3, 2];
  rows.forEach((n, r) => {
    for (let i = 0; i < n; i++) {
      const spilled = wear === 2 && r === 2;
      if (spilled) continue;
      logAt(lx + (i - (n - 1) / 2) * logR * 2.05, Y0 + logR + r * logR * 1.75, lz + (wr() - 0.5) * 0.03, 0);
    }
  });
  if (wear === 2) {
    logAt(lx + 0.06, Y0 + logR, lz + 0.3, 1.2, 0);
    logAt(lx - 0.16, Y0 + logR, lz + 0.26, -0.7, 0);
  }
  for (const k of [-1, 1]) box(g, DWOOD, 0.035, 0.24, 0.035, lx + k * 0.21, Y0 + 0.12, lz + 0.12);

  // The pallet of cut blocks, right, under the hoist.
  const px = 0.42, pz = 0.02;
  for (const z of [-0.08, 0, 0.08]) box(g, DWOOD, 0.3, 0.025, 0.06, px, Y0 + 0.0125, pz + z);
  for (const x of [-0.1, 0.1]) box(g, WOOD, 0.06, 0.02, 0.24, px + x, Y0 + 0.035, pz);
  const blocks: [number, number, number][] = [[-0.07, 0, -0.055], [0.07, 0, -0.055], [-0.07, 0, 0.065], [0.07, 0, 0.065], [0, 1, 0.005]];
  blocks.forEach(([x, layer, z], i) => {
    if (wear === 2 && i === 3) {
      const fb = box(g, BLOCK, 0.13, 0.1, 0.11, px + 0.2, Y0 + 0.05, pz + 0.22, 0.6);
      fb.rotation.z = 0.15;
      crack(g, px + 0.2, Y0 + 0.08, pz + 0.28, 0.6, 0.1, wr);
      return;
    }
    box(g, BLOCK, 0.13, 0.1, 0.11, px + x, Y0 + 0.095 + layer * 0.1, pz + z, (wr() - 0.5) * 0.12);
  });

  // The hoist: a post at the right corner, a braced jib over the pallet, a windlass and a block in a sling.
  const hx = 0.66, hz = -0.12, hTop = 0.98;
  box(g, PLINTH, 0.12, 0.05, 0.12, hx, Y0 + 0.025, hz);
  box(g, postM, 0.06, hTop - Y0, 0.06, hx, (hTop + Y0) / 2, hz);
  if (iron) for (const y of [0.3, 0.6, 0.88]) box(g, IRON_PLATE, 0.07, 0.025, 0.07, hx, y, hz);
  const root = V(hx, hTop - 0.04, hz);
  const tip = V(px - 0.02, hTop - 0.06, pz + 0.04);
  if (wear === 2) {
    const mid = root.clone().lerp(tip, 0.45);
    limb(g, root, mid, 0.022, 0.02, postM, 5);
    snapTop(g, mid.x, mid.y - 0.02, mid.z, 0.02, postM, wr);
    limb(g, mid.clone().add(V(0, -0.04, 0)), V(px - 0.06, Y0 + 0.21, pz + 0.05), 0.02, 0.018, postM, 5);
  } else {
    limb(g, root, tip, 0.022, 0.018, postM, 5);
    const pl = M(new THREE.CylinderGeometry(0.025, 0.025, 0.018, 8), DSTEEL, g);
    pl.rotation.z = Math.PI / 2;
    pl.position.set(tip.x, tip.y - 0.025, tip.z);
    const hookY = Y0 + 0.5;
    limb(g, V(tip.x, tip.y - 0.04, tip.z), V(tip.x, hookY, tip.z), 0.004, 0.004, DSTEEL, 3);
    // The sling: two ropes round a block halfway up.
    const ld = G(g, tip.x, hookY - 0.08, tip.z);
    ld.rotation.y = 0.3;
    box(ld, BLOCK, 0.12, 0.09, 0.1, 0, -0.03, 0);
    for (const k of [-1, 1]) limb(ld, V(0, 0.08, 0), V(k * 0.045, 0.015, 0), 0.003, 0.003, DWOOD, 3);
  }
  limb(g, V(hx, 0.58, hz), root.clone().lerp(tip, 0.5), 0.013, 0.013, postM, 4);
  limb(g, V(hx - 0.01, root.y - 0.02, hz + 0.02), V(hx - 0.06, 0.32, hz + 0.08), 0.003, 0.003, DSTEEL, 3);
  const drum = M(new THREE.CylinderGeometry(0.03, 0.03, 0.1, 9), WOOD, g);
  drum.rotation.z = Math.PI / 2;
  drum.position.set(hx - 0.06, 0.3, hz + 0.08);
  limb(g, V(hx - 0.115, 0.3, hz + 0.08), V(hx - 0.12, 0.36, hz + 0.12), 0.006, 0.006, DSTEEL, 3);

  // The crate of crystal shards, glowing faintly.
  const cr = G(g, 0.14, Y0, 0.26);
  cr.rotation.y = -0.3;
  box(cr, WOOD, 0.2, 0.08, 0.14, 0, 0.04, 0);
  box(cr, DWOOD, 0.21, 0.016, 0.15, 0, 0.082, 0);
  const shards = wear === 2 ? 3 : 5;
  for (let i = 0; i < shards; i++) {
    const sh = M(new THREE.OctahedronGeometry(0.03 + wr() * 0.015, 0), CRYSTAL, cr);
    sh.position.set(-0.06 + i * 0.03, 0.1 + wr() * 0.02, (wr() - 0.5) * 0.06);
    sh.scale.y = 1.7;
    sh.rotation.set((wr() - 0.5) * 0.6, wr() * 3, (wr() - 0.5) * 0.6);
  }
  if (wear === 2) {
    const spilt = M(new THREE.OctahedronGeometry(0.03, 0), CRYSTAL, cr);
    spilt.position.set(0.14, 0.02, 0.08);
    spilt.rotation.z = 1.4;
  }
  glowSprite(cr, '#7fd0ff', 0.36, V(0, 0.12, 0), [0.55, 0.45, 0.3][wear] as number);

  // The hand cart at the point of the yard, a few logs aboard.
  const ct = G(g, -0.14, Y0, 0.5);
  ct.rotation.y = 0.35;
  if (wear === 2) ct.rotation.z = -0.12;
  const bedY = 0.11;
  box(ct, WOOD, 0.3, 0.025, 0.18, 0, bedY, 0);
  for (const k of [-1, 1]) box(ct, DWOOD, 0.3, 0.05, 0.015, 0, bedY + 0.035, k * 0.09);
  box(ct, DWOOD, 0.015, 0.05, 0.18, 0.15, bedY + 0.035, 0);
  for (const k of [-1, 1]) limb(ct, V(-0.14, bedY, k * 0.07), V(-0.36, 0.015, k * 0.08), 0.01, 0.009, DWOOD, 4);
  for (const k of [-1, 1]) {
    if (wear === 2 && k > 0) {
      const off = M(new THREE.CylinderGeometry(0.075, 0.075, 0.02, 10), WOOD, ct);
      off.position.set(0.12, 0.012, 0.24);
      continue;
    }
    const wh = G(ct, 0.04, 0.075, k * 0.115);
    const tire = M(new THREE.TorusGeometry(0.068, 0.01, 4, 12), DWOOD, wh);
    tire.rotation.y = 0;
    for (let i = 0; i < 3; i++) box(wh, WOOD, 0.012, 0.13, 0.01, 0, 0, 0).rotation.z = (i * Math.PI) / 3;
  }
  for (let i = 0; i < 3; i++) {
    const lg = M(new THREE.CylinderGeometry(0.032, 0.032, 0.26, 6), WOOD, ct);
    lg.rotation.z = Math.PI / 2;
    lg.position.set(0, bedY + 0.045 + (i === 2 ? 0.05 : 0), i === 2 ? 0 : (i - 0.5) * 0.07);
  }

  if (iron) {
    boxClad(g, 0, SZ, SW, SD, Y0, Y0 + frontH, [Y0 + 0.2, Y0 + frontH - 0.03], Y0 + 0.2, ['l', 'r', 'b']);
    for (const x of [-0.3, 0.3]) plate(g, x, Y0 + backH - 0.12, back - 0.01, 0.22, 0.14, Math.PI);
  }

  if (wear > 0) {
    for (const [x, y, z, ry] of [[-0.3, 0.35, back - 0.004, Math.PI], [SW / 2 + 0.004, 0.25, SZ, Math.PI / 2], [-SW / 2 - 0.004, 0.22, SZ + 0.05, -Math.PI / 2]] as const) {
      if (mat > 1) crack(g, x, Y0 + y, z, ry, wear === 2 ? 0.22 : 0.15, wr);
      stain(g, x + 0.04, Y0 + y - 0.1, z, ry, 0.06 + wr() * 0.03, wr, '#2a231c');
    }
    splinters(g, wear * 3, 0.62, wr, 0, -0.05);
    if (mat > 1) rubbleAround(g, wear === 2 ? 7 : 3, 0.62, wr, rubbleColors(mat), 0, -0.1);
  }
  return done(c, 1.05);
};

/* ---------- the golem works ---------- */

/**
 * A golem in the making, standing in its cradle (or lying when `fallen`): `stage` 2 lacks only its left arm
 * (lying at its feet) and its eyes are lit, 1 is legs and a trunk, its head waiting on the ground and its
 * runes dark. Built like heroes.golem, one golem tall.
 */
const golemInMaking = (parent: THREE.Object3D, stage: 1 | 2, fallen: boolean): void => {
  const gg = G(parent, 0, 0, 0);
  if (fallen) {
    gg.rotation.set(-Math.PI / 2 + 0.08, 0, 0.3);
    gg.position.set(0, 0.11, 0.06);
  }
  M(new THREE.BoxGeometry(0.26, 0.28, 0.2), GOLEM, gg).position.y = 0.32;
  for (const k of [-1, 1]) limb(gg, V(k * 0.07, 0.18, 0), V(k * 0.07, 0, 0), 0.05, 0.055, GOLEM, 6);
  box(gg, stage === 2 ? RUNE : BLACK, 0.08, 0.02, 0.01, 0, 0.34, 0.105);
  if (stage === 2) {
    M(new THREE.BoxGeometry(0.14, 0.12, 0.14), GOLEM, gg).position.set(0, 0.53, 0.02);
    for (const k of [-1, 1]) box(gg, RUNE, 0.03, 0.02, 0.01, k * 0.035, 0.54, 0.095);
    limb(gg, V(0.16, 0.42, 0), V(0.18, 0.2, 0.05), 0.045, 0.05, GOLEM, 6);
    // The rough shoulder socket where the left arm goes.
    M(new THREE.DodecahedronGeometry(0.04, 0), RUNE_STONE, gg).position.set(-0.15, 0.42, 0);
  } else {
    // An open socket on top, iron pins standing in it.
    for (const k of [-1, 1]) limb(gg, V(k * 0.03, 0.46, 0), V(k * 0.03, 0.52, 0), 0.008, 0.008, DSTEEL, 4);
  }
};

/**
 * A golem cradle: a timber frame (iron in material 3) of two posts on sills, braced, a beam across and chains
 * holding the figure up; `snapped` drops one end of the beam.
 */
const cradle = (parent: THREE.Object3D, x: number, z: number, ry: number, mat: Era2Material, stage: 1 | 2, snapped: boolean, rng: () => number): void => {
  const cg = G(parent, x, Y0, z);
  cg.rotation.y = ry;
  const fm = mat === 3 ? IRON_BAND : DWOOD;
  const H = 0.66, half = 0.22;
  for (const k of [-1, 1]) {
    box(cg, fm, 0.06, 0.04, 0.3, k * half, 0.02, 0);
    const broken = snapped && k > 0;
    const ph = broken ? 0.36 : H;
    box(cg, fm, 0.05, ph, 0.05, k * half, ph / 2, 0);
    if (broken) snapTop(cg, k * half, ph, 0, 0.025, fm, rng);
    for (const s of [-1, 1]) limb(cg, V(k * half, 0.03, s * 0.13), V(k * half, 0.24, 0), 0.012, 0.012, fm, 4);
    if (mat === 3) for (const y of [0.2, 0.45]) if (y < ph) box(cg, IRON_RIVET, 0.06, 0.012, 0.06, k * half, y, 0);
  }
  const beam = G(cg, 0, H, 0);
  if (snapped) {
    beam.rotation.z = -0.55;
    beam.position.set(-0.03, H - 0.1, 0);
  }
  box(beam, fm, half * 2 + 0.1, 0.05, 0.06, 0, 0, 0);
  if (!snapped) for (const k of [-1, 1]) limb(cg, V(k * 0.07, H - 0.02, 0), V(k * 0.1, 0.44, 0), 0.006, 0.006, DSTEEL, 3);
  golemInMaking(cg, stage, snapped);
  if (stage === 2) {
    // The left arm waits on the ground.
    limb(cg, V(-0.2, 0.04, 0.2), V(0.0, 0.035, 0.24), 0.045, 0.05, GOLEM, 6);
  } else {
    const hd = box(cg, GOLEM, 0.14, 0.12, 0.14, -0.06, 0.06, 0.22, 0.5);
    for (const k of [-1, 1]) box(hd, BLACK, 0.03, 0.02, 0.01, k * 0.035, 0.01, 0.072);
  }
};

/** A stack of dressed blocks carved with runes that glow the golems' cyan. */
const runeBlocks = (parent: THREE.Object3D, x: number, z: number, ry: number, n: 2 | 3): void => {
  const rg = G(parent, x, Y0, z);
  rg.rotation.y = ry;
  const spots: [number, number, number, number][] = [[-0.07, 0.05, 0, 0.05], [0.07, 0.05, 0.01, -0.08], [0, 0.15, 0, 0.2]];
  spots.slice(0, n).forEach(([bx, by, bz, r]) => {
    const b = box(rg, RUNE_STONE, 0.13, 0.1, 0.11, bx, by, bz, r);
    box(b, RUNE, 0.012, 0.06, 0.006, 0, 0, 0.057);
    box(b, RUNE, 0.05, 0.012, 0.006, 0, 0.018, 0.057);
  });
  glowSprite(rg, '#4fdcff', 0.22, V(0, 0.1, 0.1), 0.45);
};

/**
 * The golem works: a heavy stone workshop across the back of the yard, its forge burning through an arched
 * mouth, a pair of ironbound doors beside it; in front, a golem stands half-built in a timber cradle,
 * chained up by the shoulders, and an anvil on its stump waits by a stack of rune-carved blocks. It grows
 * by modules: level 2 a second cradle with a golem barely begun, level 3 a tall chimney ringed with glowing
 * runes. Fieldstone and timber in wood, dressed stone in stone, plated and banded with an iron roof in iron.
 * Worn (1): cracks, soot, gaps in the roof. Crumbling (2): the roof holed, the chimney snapped, a cradle
 * broken and its golem fallen on its back, the forge burning low.
 */
export const golemWorks = (o?: GolemWorksOpts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const level = o?.level ?? 1;
  const c = begin(o, 0.85, 141);
  const { g } = c;
  const wr = mulberry32(1410 + mat * 3 + wear + level * 11);
  const iron = mat === 3;
  yard(g, mat);

  const W = 0.98, D = 0.44, Z = -0.36, H = 0.5;
  const front = Z + D / 2;
  const wallM = wallOf(mat);
  box(g, wallM, W, H, D, 0, Y0 + H / 2, Z);
  if (mat === 1) {
    fieldstones(g, 12, -W / 2 + 0.05, W / 2 - 0.05, Y0 + 0.04, Y0 + H - 0.06, front + 0.005, wr);
    // A timber plate under the eaves and posts at the corners.
    box(g, DWOOD, W + 0.03, 0.04, D + 0.03, 0, Y0 + H - 0.02, Z);
    for (const sx of [-1, 1]) for (const sz of [-1, 1]) box(g, DWOOD, 0.045, H, 0.045, (sx * W) / 2, Y0 + H / 2, Z + (sz * D) / 2);
  } else {
    quoins(g, quoinOf(mat), 0, Z, W, D, Y0, Y0 + H);
    box(g, stone(iron ? '#5d6167' : '#6f6a63'), W + 0.03, 0.035, D + 0.03, 0, Y0 + H - 0.015, Z);
  }

  // The roof.
  const roofM = iron ? IRON_ROOF : mat === 1 ? PLANK : stone('#3e3a36');
  const span = D + 0.04, rh = 0.28, len = W + 0.04;
  const roof = pitchedRoof(g, 0, Y0 + H, Z, span, rh, len, roofM, wallM);
  if (iron) seams(roof.grp, span, rh, len + 0.06, roof.angle);
  else if (mat === 1) {
    for (const side of [1, -1]) for (let i = 1; i < 4; i++) {
      const k = i / 4;
      const s = box(roof.grp, PLANK_DK, len + 0.08, 0.012, 0.016, 0, rh * (1 - k) + 0.035, side * (span / 2) * k + side * 0.012);
      s.rotation.x = side * roof.angle;
    }
  }
  for (let i = 0; i < wear * 2; i++) {
    const side = i % 2 ? -1 : 1;
    const k = 0.3 + wr() * 0.45;
    const gap = box(roof.grp, GAP, 0.09 + wr() * 0.08, 0.01, 0.07, (wr() - 0.5) * 0.7, rh * (1 - k) + 0.04, side * (span / 2) * k);
    gap.rotation.x = side * roof.angle;
  }
  if (wear === 2) {
    const hole = box(roof.grp, GAP, 0.26, 0.012, 0.16, 0.22, rh * 0.5 + 0.035, span / 4 + 0.01);
    hole.rotation.x = roof.angle;
  }

  // The forge mouth: an arch of voussoirs over a fire-lit opening, the heat glowing out.
  const fx = -0.2, fy = Y0, fz = front + 0.004;
  const fire = [1, 0.75, 0.4][wear] as number;
  box(g, BLACK, 0.26, 0.2, 0.02, fx, fy + 0.1, fz);
  const arch = M(new THREE.CircleGeometry(0.13, 10, 0, Math.PI), BLACK, g);
  arch.position.set(fx, fy + 0.2, fz + 0.011);
  box(g, FORGE, 0.2, 0.08, 0.012, fx, fy + 0.05, fz + 0.008);
  box(g, EMBER, 0.22, 0.12, 0.01, fx, fy + 0.13, fz + 0.006);
  const vous = M(new THREE.TorusGeometry(0.155, 0.032, 4, 10, Math.PI), mat === 1 ? PLINTH : quoinOf(mat), g);
  vous.position.set(fx, fy + 0.2, fz + 0.016);
  for (const k of [-1, 1]) box(g, mat === 1 ? PLINTH : quoinOf(mat), 0.06, 0.2, 0.05, fx + k * 0.155, fy + 0.1, fz + 0.012);
  glowSprite(g, '#ff7a2a', 0.55, V(fx, fy + 0.14, fz + 0.08), 0.6 * fire);
  // Iron-bound doors right of the forge.
  const dx = 0.2;
  box(g, BLACK, 0.24, 0.32, 0.012, dx, Y0 + 0.16, front + 0.003);
  for (const k of [-1, 1]) {
    const leaf = box(g, DWOOD, 0.11, 0.3, 0.02, dx + k * 0.058, Y0 + 0.15, front + 0.012);
    leaf.rotation.y = k * (wear === 2 ? 0.25 : 0.0);
    for (const y of [-0.09, 0.09]) box(leaf, IRON_BAND, 0.11, 0.022, 0.008, 0, y, 0.012);
  }
  box(g, mat === 1 ? DWOOD : quoinOf(mat), 0.32, 0.05, 0.05, dx, Y0 + 0.34, front + 0.012);
  // A cyan rune carved over the doors: the works' sign.
  box(g, RUNE, 0.012, 0.07, 0.006, dx, Y0 + 0.42, front + 0.004);
  box(g, RUNE, 0.06, 0.012, 0.006, dx, Y0 + 0.44, front + 0.004);
  // A window on the right gable end with the warm light.
  box(g, BLACK, 0.012, 0.1, 0.1, W / 2 + 0.003, Y0 + 0.3, Z);
  box(g, EMBER, 0.012, 0.04, 0.08, W / 2 + 0.005, Y0 + 0.28, Z);

  // The chimney at the back-left: short and squat, or (level 3) a tall stack ringed with runes.
  const chx = -0.32, chz = Z - 0.06;
  const tall = level === 3;
  const fullTop = tall ? 1.78 : 1.12;
  const chTop = wear === 2 ? (tall ? 1.15 : 0.9) : fullTop;
  const chW = tall ? 0.22 : 0.18;
  box(g, wallM, chW, chTop - Y0 - 0.2, chW, chx, (chTop + Y0 + 0.2) / 2, chz);
  if (tall) {
    // A broad footing, then rune bands up the stack.
    box(g, mat === 1 ? PLINTH : quoinOf(mat), chW + 0.06, 0.18, chW + 0.06, chx, Y0 + H + 0.09, chz);
    for (const y of [0.98, 1.3, 1.62]) {
      if (y > chTop - 0.06) continue;
      const band = M(new THREE.CylinderGeometry(chW * 0.72, chW * 0.72, 0.035, 4, 1, true), RUNE, g);
      band.position.set(chx, y, chz);
      band.rotation.y = Math.PI / 4;
      glowSprite(g, '#4fdcff', 0.3, V(chx + 0.05, y, chz + chW / 2 + 0.04), 0.4);
    }
    if (iron) for (const y of [1.14, 1.46]) if (y < chTop - 0.06) box(g, IRON_BAND, chW + 0.015, 0.03, chW + 0.015, chx, y, chz);
  } else if (iron) box(g, IRON_BAND, chW + 0.015, 0.03, chW + 0.015, chx, 0.95, chz);
  if (wear === 2) {
    snapTop(g, chx, chTop, chz, 0.08, wallM, wr);
    const top = box(g, wallM, chW, 0.24, chW, -0.62, 0.11, 0.05);
    top.rotation.set(0.3, 0.6, Math.PI / 2);
  } else {
    box(g, wallM, chW + 0.05, 0.05, chW + 0.05, chx, chTop, chz);
    box(g, BLACK, chW - 0.06, 0.02, chW - 0.06, chx, chTop + 0.02, chz);
    if (tall) {
      // An iron crown of four horns and a cyan glow in the throat.
      for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        const horn = M(new THREE.ConeGeometry(0.025, 0.12, 4), IRON_BAND, g);
        horn.position.set(chx + sx * (chW / 2 + 0.01), chTop + 0.08, chz + sz * (chW / 2 + 0.01));
      }
      box(g, RUNE, chW - 0.08, 0.012, chW - 0.08, chx, chTop + 0.032, chz);
      glowSprite(g, '#4fdcff', 0.4, V(chx, chTop + 0.08, chz), 0.5);
    } else {
      box(g, EMBER, chW - 0.08, 0.012, chW - 0.08, chx, chTop + 0.032, chz);
    }
  }

  // Cradles: the first at the right, the second (level 2) at the left; otherwise the left holds raw stone
  // and a head on a block, waiting.
  cradle(g, 0.42, 0.06, -0.25, mat, 2, wear === 2, wr);
  if (level >= 2) cradle(g, -0.42, 0.06, 0.25, mat, 1, false, wr);
  else {
    for (let i = 0; i < 5; i++) {
      const r = M(new THREE.DodecahedronGeometry(0.06 + wr() * 0.03, 0), i % 2 ? GOLEM : PLINTH, g);
      r.position.set(-0.42 + (wr() - 0.5) * 0.22, Y0 + 0.05 + (i > 2 ? 0.07 : 0), -0.02 + (wr() - 0.5) * 0.14);
      r.rotation.set(wr(), wr() * 3, wr());
    }
    box(g, RUNE_STONE, 0.15, 0.12, 0.13, -0.5, Y0 + 0.06, 0.2, 0.3);
    const hd = box(g, GOLEM, 0.14, 0.12, 0.14, -0.5, Y0 + 0.18, 0.2, 0.5);
    for (const k of [-1, 1]) box(hd, RUNE, 0.03, 0.02, 0.01, k * 0.035, 0.01, 0.072);
  }

  // The anvil on its stump, a hammer across it, the rune blocks beside.
  const an = G(g, 0.12, Y0, 0.46);
  an.rotation.y = -0.4;
  M(new THREE.CylinderGeometry(0.075, 0.085, 0.12, 8), WOOD, an).position.y = 0.06;
  box(an, DSTEEL, 0.1, 0.03, 0.07, 0, 0.135, 0);
  box(an, DSTEEL, 0.06, 0.04, 0.05, 0, 0.17, 0);
  box(an, DSTEEL, 0.16, 0.035, 0.075, 0, 0.205, 0);
  const horn = M(new THREE.ConeGeometry(0.035, 0.09, 6), DSTEEL, an);
  horn.rotation.z = Math.PI / 2;
  horn.position.set(0.12, 0.205, 0);
  const hm = G(an, -0.02, 0.23, 0.0);
  hm.rotation.y = 0.8;
  box(hm, WOOD, 0.16, 0.014, 0.014, 0.03, 0, 0);
  box(hm, DSTEEL, 0.03, 0.04, 0.05, -0.05, 0.008, 0);
  runeBlocks(g, -0.14, 0.44, 0.3, level >= 2 ? 3 : 2);

  if (iron) {
    boxClad(g, 0, Z, W, D, Y0, Y0 + H, [Y0 + 0.2, Y0 + H - 0.04], Y0 + 0.2, ['l', 'b', 'r']);
    for (const x of [-W / 2 + 0.07, W / 2 - 0.08]) plate(g, x, Y0 + 0.11, front + 0.009, 0.1, 0.18, 0);
  }

  if (wear > 0) {
    for (const [x, z, ry] of [[-0.4, front + 0.004, 0], [0.38, front + 0.004, 0], [W / 2 + 0.004, Z + 0.1, Math.PI / 2], [-W / 2 - 0.004, Z - 0.1, -Math.PI / 2]] as const) {
      if (mat > 1 || wr() < 0.5) crack(g, x, Y0 + 0.3, z, ry, wear === 2 ? 0.24 : 0.16, wr);
      stain(g, x + 0.05, Y0 + 0.16, z, ry, 0.06 + wr() * 0.04, wr, '#26221f');
    }
    stain(g, fx, Y0 + 0.4, fz + 0.012, 0, 0.09, wr, '#1e1a17');
    rubbleAround(g, wear === 2 ? 9 : 4, 0.66, wr, rubbleColors(mat), 0, -0.1);
    splinters(g, wear * 2, 0.6, wr, 0, -0.05);
  }

  const base = [0.4, 0.3, 0.16][wear] as number;
  const L = pointLight(c, 0xff8a3a, base, 1.5, V(fx, Y0 + 0.16, fz + 0.22));
  const tick = (n: number): void => {
    L.intensity = base * (1 + (n % 5 < 2 ? 0.18 : 0) - (n % 7 === 3 ? 0.12 : 0)) * LIGHT_K;
  };
  return done(c, tall && wear < 2 ? 1.9 : 1.3, tick);
};

/* ---------- ruins ---------- */

/** What a destroyed gathering post or golem works leaves, in its material. */
export const landRuin = (type: string, mat: Era2Material = 2): Model => {
  const c = begin(undefined, 0.8, 151);
  const { g } = c;
  const rng = mulberry32(1510 + type.length * 13 + mat * 5);
  const st = wallOf(mat);
  const colors = rubbleColors(mat);
  yard(g, mat);
  const scorch = (r: number, x: number, z: number, sx: number): void => {
    const d = M(new THREE.CircleGeometry(r, 12), std('#1e1a16', { roughness: 1, bumpScale: 0 }), g);
    d.rotation.x = -Math.PI / 2;
    d.scale.x = sx;
    d.position.set(x, Y0 + 0.004, z);
  };
  const stubs = (cx: number, cz: number, w: number, d: number, sides: ('f' | 'b' | 'l' | 'r')[], wood: boolean): void => {
    const segs: [number, number, number, number, string][] = [[cx, cz + d / 2, w, 0.06, 'f'], [cx, cz - d / 2, w, 0.06, 'b'], [cx - w / 2, cz, 0.06, d, 'l'], [cx + w / 2, cz, 0.06, d, 'r']];
    for (const [x, z, ww, dd, side] of segs) {
      if (!sides.includes(side as 'f')) continue;
      const n = Math.max(2, Math.round(Math.max(ww, dd) / 0.18));
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n - 0.5;
        const sh = 0.05 + rng() * (wood ? 0.16 : 0.2);
        box(g, wood ? std('#2b2119', { roughness: 1 }) : st, ww > dd ? ww / n - 0.012 : ww, sh, dd > ww ? dd / n - 0.012 : dd, x + (ww > dd ? t * ww : 0), Y0 + sh / 2, z + (dd > ww ? t * dd : 0));
      }
    }
  };
  const ironBits = (): void => {
    if (mat !== 3) return;
    for (let i = 0; i < 5; i++) {
      const a = rng() * TAU;
      const r = 0.3 + rng() * 0.3;
      const p = box(g, IRON_PLATE, 0.12 + rng() * 0.08, 0.012, 0.09 + rng() * 0.05, Math.sin(a) * r, Y0 + 0.02 + rng() * 0.03, Math.cos(a) * r - 0.1, rng() * 3);
      p.rotation.x = (rng() - 0.5) * 0.6;
    }
    const band = M(new THREE.TorusGeometry(0.16, 0.014, 4, 12, Math.PI * 0.9), IRON_BAND, g);
    band.position.set(0.3, Y0 + 0.03, -0.35);
    band.rotation.set(-Math.PI / 2 + 0.15, 0, rng() * 3);
  };
  let h: number;
  if (type === 'depot') {
    scorch(0.42, 0, -0.3, 1.4);
    stubs(0, -0.37, 1.04, 0.4, ['b', 'l', 'r'], mat === 1);
    // Charred roof planks, logs rolled across the yard, the pallet's blocks tumbled, a few shards still faintly lit.
    for (let i = 0; i < 5; i++) {
      const p = box(g, i % 2 ? std('#2b2119', { roughness: 1 }) : PLANK, 0.06, 0.02, 0.4 + rng() * 0.2, -0.35 + i * 0.17, Y0 + 0.03 + (i % 2) * 0.02, -0.3 + (rng() - 0.5) * 0.15, 0.2 + rng() * 0.5);
      p.rotation.z = (rng() - 0.5) * 0.3;
    }
    for (let i = 0; i < 5; i++) {
      const lg = G(g, -0.5 + rng() * 0.25, Y0 + 0.045, -0.05 + rng() * 0.35);
      lg.rotation.y = rng() * 3;
      const cy = M(new THREE.CylinderGeometry(0.045, 0.047, 0.34, 7), i === 0 ? std('#2b2119', { roughness: 1 }) : WOOD, lg);
      cy.rotation.x = Math.PI / 2;
    }
    for (let i = 0; i < 4; i++) {
      const b = box(g, BLOCK, 0.13, 0.1, 0.11, 0.3 + rng() * 0.25, Y0 + 0.05, -0.05 + rng() * 0.3, rng() * 3);
      b.rotation.z = (rng() - 0.5) * 0.4;
    }
    for (let i = 0; i < 3; i++) {
      const sh = M(new THREE.OctahedronGeometry(0.03, 0), CRYSTAL, g);
      sh.position.set(0.05 + i * 0.07, Y0 + 0.02, 0.3 + (rng() - 0.5) * 0.08);
      sh.rotation.set(rng(), rng() * 3, 1.3);
      sh.scale.y = 1.6;
    }
    glowSprite(g, '#7fd0ff', 0.26, V(0.12, Y0 + 0.05, 0.3), 0.3);
    // The hoist's post snapped low, its jib across the pallet.
    box(g, std('#2b2119', { roughness: 1 }), 0.06, 0.22, 0.06, 0.66, Y0 + 0.11, -0.12);
    snapTop(g, 0.66, Y0 + 0.22, -0.12, 0.03, DWOOD, rng);
    limb(g, V(0.62, Y0 + 0.03, 0.05), V(0.2, Y0 + 0.05, 0.15), 0.022, 0.018, DWOOD, 5);
    splinters(g, 8, 0.5, rng, 0, -0.1);
    if (mat > 1) rubbleAround(g, 8, 0.55, rng, colors, 0, -0.15);
    h = 0.32;
  } else {
    scorch(0.5, 0, -0.3, 1.3);
    stubs(0, -0.36, 0.98, 0.44, ['f', 'b', 'l', 'r'], false);
    // The forge's arch still standing in part, a golem on its back, the anvil, the chimney's stack fallen.
    box(g, mat === 1 ? PLINTH : quoinOf(mat), 0.06, 0.26, 0.06, -0.36, Y0 + 0.13, -0.14);
    box(g, FORGE, 0.14, 0.03, 0.08, -0.2, Y0 + 0.02, -0.2);
    glowSprite(g, '#ff6a1a', 0.3, V(-0.2, Y0 + 0.05, -0.15), 0.35);
    const fallen = G(g, 0.35, Y0, 0.15);
    fallen.rotation.y = -0.6;
    golemInMaking(fallen, 1, true);
    const an = G(g, 0.0, Y0, 0.4);
    M(new THREE.CylinderGeometry(0.075, 0.085, 0.1, 8), std('#2b2119', { roughness: 1 }), an).position.y = 0.05;
    const ab = box(an, DSTEEL, 0.16, 0.06, 0.075, 0.12, 0.03, 0.08, 0.7);
    ab.rotation.z = 0.2;
    const stack = box(g, st, 0.16, 0.16, 0.4, -0.5, Y0 + 0.08, 0.22, 1.1);
    stack.rotation.x = 0.1;
    for (let i = 0; i < 3; i++) {
      const b = box(g, RUNE_STONE, 0.13, 0.1, 0.11, -0.15 + i * 0.12, Y0 + 0.05, 0.25 + rng() * 0.1, rng() * 3);
      if (i === 1) box(b, RUNE, 0.012, 0.06, 0.006, 0, 0, 0.057);
    }
    rubbleAround(g, 10, 0.55, rng, colors, 0, -0.15);
    splinters(g, 5, 0.5, rng, 0, -0.1);
    h = 0.38;
  }
  ironBits();
  return done(c, h);
};
